import React, { useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { useAppDispatch, useAppSelector } from 'store';

import { resetRedirectUrl, setRedirectUrl } from 'modules/auth';
import { globalConst } from 'modules/global';

import { IOlympicList } from '../models';

import TimerBox from './timer-box';

interface IProps {
  activeOlympic?: IOlympicList[];
  isLoading?: boolean;
}

const Banner: React.FC<IProps> = ({ activeOlympic, isLoading }) => {
  const { asPath } = useRouter();
  const isAuth = useAppSelector(({ user }) => user?.phone);
  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(resetRedirectUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Link
      onClick={() => dispatch(setRedirectUrl(asPath))}
      href={
        isAuth
          ? `${globalConst.pageNames.ONLINE_OLYMPIC}/olimpiada-${activeOlympic?.[0]?.id}`
          : globalConst?.pageNames?.LOGIN
      }
      title={activeOlympic?.[0]?.name}
    >
      <div className="relative mt-2 h-40 w-full overflow-hidden rounded-xl bg-gradient-to-r from-[#F6D991] to-[#FFF2D3]">
        <div
          className="absolute inset-0 z-0 bg-[url('/images/olympic-banner.webp')] bg-cover bg-center"
          title="Virtual test"
        />

        <div className="relative z-10 flex p-3 sm:p-4">
          {isLoading ? (
            <div role="status" className="flex h-36 w-1/2 max-w-sm animate-pulse flex-col justify-between sm:h-32">
              <div>
                <div className="mb-2 h-5 rounded-md bg-white/70 backdrop-blur-lg" />
                <div className="mb-2 h-3 w-2/3 rounded-md bg-white/70 backdrop-blur-lg" />
              </div>
              <div className="mb-2 h-7 rounded-md bg-white/70 backdrop-blur-lg" />
            </div>
          ) : (
            <div className="h-35 flex flex-col justify-between sm:h-32">
              <div>
                <h2 className="line-clamp-1 text-xl font-bold text-black">
                  {activeOlympic?.[0]?.name || 'Turnir mavjud emas'}
                </h2>
                {activeOlympic?.[0]?.title ? (
                  <p className="w-50 line-clamp-2 max-w-xl text-xs text-gray-700 sm:w-full sm:text-sm">
                    {activeOlympic?.[0]?.title}
                  </p>
                ) : null}
              </div>
              <TimerBox
                forBanner
                status={activeOlympic?.[0]?.status}
                time={
                  activeOlympic?.[0]?.status === 'active'
                    ? activeOlympic?.[0]?.end_time
                    : activeOlympic?.[0]?.started_time
                }
              />
            </div>
          )}

          <div>
            {isLoading ? (
              <div className="rotate-5 sm:w-55 sm:h-39 w-47 h-31 absolute right-2 top-10 flex w-full items-center justify-center rounded-2xl bg-white/70 sm:right-5 sm:top-4 md:right-20">
                <svg
                  className="h-10 w-10 text-gray-200"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 20 18"
                >
                  <path d="M18 0H2a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2Zm-5.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3Zm4.376 10.481A1 1 0 0 1 16 15H4a1 1 0 0 1-.895-1.447l3.5-7A1 1 0 0 1 7.468 6a.965.965 0 0 1 .9.5l2.775 4.757 1.546-1.887a1 1 0 0 1 1.618.1l2.541 4a1 1 0 0 1 .028 1.011Z" />
                </svg>
              </div>
            ) : (
              <Image
                width={220}
                height={156}
                src="/images/olympic.webp"
                alt="Olympic symbol"
                title="Olimpiada rasmi"
                className="sm:w-55 sm:h-39 w-47 h-31 absolute right-2 top-10 sm:right-5 sm:top-4 md:right-20"
              />
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default Banner;
